"use client";

import { useRef, useState } from "react";
import type { UseFormReturn } from "react-hook-form";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  NativeSelect,
  Textarea,
} from "@stocks/ui";
import type { ItemResponse, StockInResultResponse } from "@stocks/shared";

import {
  ItemCategoryBadge,
  ItemThumbnail,
} from "@/entities/item";
import {
  formatQuantity,
  useLocationOptions,
  useLotOptions,
  useVendorOptions,
} from "@/entities/stock";
import { useCreateStockIn } from "@/features/stock/create-stock-in";
import { useCreateVendor } from "@/features/stock/create-vendor";

import {
  toCreateStockInRequest,
  todayString,
  type LocationMode,
  type LotMode,
  type StockInFormValues,
  type VendorMode,
} from "../lib/stockInForm";

interface ReceiveFormProps {
  item: ItemResponse;
  form: UseFormReturn<StockInFormValues>;
  onChangeItem: () => void;
  onDone: (result: StockInResultResponse) => void;
}

const ModeToggle = <T extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
  label: string;
}) => (
  <div role='group' aria-label={label} className='flex flex-wrap gap-1'>
    {options.map((o) => (
      <Button
        key={o.value}
        type='button'
        size='sm'
        variant={value === o.value ? "default" : "outline"}
        aria-pressed={value === o.value}
        onClick={() => onChange(o.value)}
      >
        {o.label}
      </Button>
    ))}
  </div>
);

const required = (message: string) => (v: string) =>
  v.trim() !== "" || message;

/** 선택한 품목의 공급업체·입고일·수량·로트·보관 위치를 입력받아 입고를 확정한다. */
export const ReceiveForm = ({
  item,
  form,
  onChangeItem,
  onDone,
}: ReceiveFormProps) => {
  const {
    register,
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
  } = form;
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // 멱등 키: 네트워크 단절 후 재시도는 같은 키, 서버가 응답으로 거절한 뒤에는 새 키.
  const requestIdRef = useRef(crypto.randomUUID());
  const createdVendorRef = useRef<{ code: string; id: string } | null>(null);

  const vendors = useVendorOptions();
  const locations = useLocationOptions();
  const lots = useLotOptions(item.id);
  const createVendor = useCreateVendor();
  const createStockIn = useCreateStockIn();

  const vendorMode = watch("vendorMode");
  const lotMode = watch("lotMode");
  const locationMode = watch("locationMode");
  const quantity = watch("quantity");
  const isBusy = createVendor.isPending || createStockIn.isPending;

  const confirmStockIn = (values: StockInFormValues, vendorId: string) =>
    createStockIn.mutate(
      toCreateStockInRequest(values, {
        itemId: item.id,
        vendorId,
        requestId: requestIdRef.current,
      }),
      {
        onSuccess: (res) => {
          const result = res.data.data;
          if (!result) return;
          requestIdRef.current = crypto.randomUUID();
          onDone(result);
        },
        onError: (err) => {
          setSubmitError(
            err.response?.data.message ??
              "서버에 연결하지 못했습니다. 입력한 내용은 유지됩니다. 잠시 후 다시 시도하세요.",
          );
          if (err.response) requestIdRef.current = crypto.randomUUID();
        },
      },
    );

  const submit = (values: StockInFormValues) => {
    setConfirmOpen(false);
    setSubmitError(null);

    if (values.vendorMode === "existing") {
      confirmStockIn(values, values.vendorId);
      return;
    }
    const code = values.newVendorCode.trim();
    // 업체만 등록되고 입고가 실패한 뒤 재시도할 때 중복 등록을 막는다.
    if (createdVendorRef.current?.code === code) {
      confirmStockIn(values, createdVendorRef.current.id);
      return;
    }
    createVendor.mutate(
      { code, name: values.newVendorName.trim() },
      {
        onSuccess: (res) => {
          const created = res.data.data;
          if (!created) return;
          createdVendorRef.current = { code, id: created.id };
          confirmStockIn(values, created.id);
        },
      },
    );
  };

  const vendorName =
    vendorMode === "new"
      ? watch("newVendorName")
      : vendors.data?.items.find((v) => v.id === watch("vendorId"))?.name;

  return (
    <Card>
      <CardHeader>
        <CardTitle>2. 입고 정보 입력</CardTitle>
      </CardHeader>
      <CardContent className='flex flex-col gap-5'>
        <div className='flex flex-wrap items-center gap-3 rounded-lg border p-3'>
          <ItemThumbnail path={item.imageUrl} alt={item.name} className='size-16' />
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            <div className='flex flex-wrap items-center gap-2'>
              <span className='font-semibold'>{item.name}</span>
              <ItemCategoryBadge category={item.category} />
            </div>
            <span className='text-sm text-muted-foreground'>
              {item.code} · 규격 {item.spec ?? "-"} · 단위 {item.baseUnit}
            </span>
          </div>
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={isBusy}
            onClick={onChangeItem}
          >
            품목 변경
          </Button>
        </div>

        <form
          onSubmit={handleSubmit(() => setConfirmOpen(true))}
          className='flex flex-col gap-5'
          noValidate
        >
          {/* 공급업체 */}
          <section className='flex flex-col gap-2'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <span className='text-sm font-medium'>공급업체</span>
              <ModeToggle<VendorMode>
                label='공급업체 입력 방식'
                value={vendorMode}
                onChange={(v) => setValue("vendorMode", v)}
                options={[
                  { value: "existing", label: "기존 업체 선택" },
                  { value: "new", label: "신규 등록" },
                ]}
              />
            </div>
            {vendorMode === "existing" ? (
              <Field>
                <NativeSelect
                  aria-label='공급업체'
                  className='w-full'
                  {...register("vendorId", {
                    validate: (v) =>
                      watch("vendorMode") !== "existing" ||
                      v !== "" ||
                      "공급업체를 선택하세요.",
                  })}
                >
                  <option value=''>
                    {vendors.isLoading ? "불러오는 중…" : "선택"}
                  </option>
                  {vendors.data?.items.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} ({v.code})
                    </option>
                  ))}
                </NativeSelect>
                {vendors.isError && (
                  <FieldError>공급업체 목록을 불러오지 못했습니다.</FieldError>
                )}
                {vendors.data?.items.length === 0 && (
                  <p className='text-xs text-muted-foreground'>
                    등록된 공급업체가 없습니다. 신규 등록을 선택하세요.
                  </p>
                )}
                {errors.vendorId && <FieldError>{errors.vendorId.message}</FieldError>}
              </Field>
            ) : (
              <div className='grid gap-3 sm:grid-cols-2'>
                <Field>
                  <FieldLabel htmlFor='new-vendor-code'>업체 코드</FieldLabel>
                  <Input
                    id='new-vendor-code'
                    {...register("newVendorCode", {
                      validate: (v) =>
                        watch("vendorMode") !== "new" ||
                        required("업체 코드를 입력하세요.")(v),
                    })}
                  />
                  {errors.newVendorCode && (
                    <FieldError>{errors.newVendorCode.message}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor='new-vendor-name'>업체명</FieldLabel>
                  <Input
                    id='new-vendor-name'
                    {...register("newVendorName", {
                      validate: (v) =>
                        watch("vendorMode") !== "new" ||
                        required("업체명을 입력하세요.")(v),
                    })}
                  />
                  {errors.newVendorName && (
                    <FieldError>{errors.newVendorName.message}</FieldError>
                  )}
                </Field>
              </div>
            )}
          </section>

          {/* 입고일 / 수량 */}
          <section className='grid gap-3 sm:grid-cols-2'>
            <Field>
              <FieldLabel htmlFor='received-at'>입고일</FieldLabel>
              <Input
                id='received-at'
                type='date'
                max={todayString()}
                {...register("receivedAt", {
                  validate: (v) =>
                    (v !== "" && v <= todayString()) ||
                    "오늘 이전 날짜를 입력하세요.",
                })}
              />
              {errors.receivedAt && (
                <FieldError>{errors.receivedAt.message}</FieldError>
              )}
            </Field>
            <Field>
              <FieldLabel htmlFor='quantity'>
                입고 수량 ({item.baseUnit})
              </FieldLabel>
              <Input
                id='quantity'
                type='number'
                inputMode='decimal'
                min={0}
                step='any'
                {...register("quantity", {
                  validate: (v) =>
                    (v !== "" && Number(v) > 0) || "0보다 큰 수량을 입력하세요.",
                })}
              />
              {errors.quantity && <FieldError>{errors.quantity.message}</FieldError>}
            </Field>
          </section>

          {/* 로트 */}
          <section className='flex flex-col gap-2'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <span className='text-sm font-medium'>로트</span>
              <ModeToggle<LotMode>
                label='로트 입력 방식'
                value={lotMode}
                onChange={(v) => setValue("lotMode", v)}
                options={[
                  { value: "new", label: "새 로트 등록" },
                  { value: "existing", label: "기존 로트에 추가" },
                ]}
              />
            </div>
            {lotMode === "new" ? (
              <div className='grid gap-3 sm:grid-cols-2'>
                <Field>
                  <FieldLabel htmlFor='supplier-lot-no'>
                    공급업체 로트 번호 (선택)
                  </FieldLabel>
                  <Input id='supplier-lot-no' {...register("supplierLotNo")} />
                  <p className='text-xs text-muted-foreground'>
                    내부 추적 번호는 확정 시 자동 발급됩니다.
                  </p>
                </Field>
                <Field>
                  <FieldLabel htmlFor='expires-at'>유효기한 (선택)</FieldLabel>
                  <Input id='expires-at' type='date' {...register("expiresAt")} />
                </Field>
              </div>
            ) : (
              <Field>
                <NativeSelect
                  aria-label='기존 로트'
                  className='w-full'
                  {...register("lotId", {
                    validate: (v) =>
                      watch("lotMode") !== "existing" ||
                      v !== "" ||
                      "로트를 선택하세요.",
                  })}
                >
                  <option value=''>
                    {lots.isLoading ? "불러오는 중…" : "선택"}
                  </option>
                  {lots.data?.items.map((l) => (
                    <option key={l.lotId} value={l.lotId}>
                      {l.lotNo}
                      {l.supplierLotNo ? ` / ${l.supplierLotNo}` : ""} · 보유{" "}
                      {formatQuantity(l.onHand)} {item.baseUnit}
                      {l.location ? ` · ${l.location.code}` : ""}
                    </option>
                  ))}
                </NativeSelect>
                {lots.data?.items.length === 0 && (
                  <p className='text-xs text-muted-foreground'>
                    이 품목의 기존 로트가 없습니다. 새 로트를 등록하세요.
                  </p>
                )}
                {errors.lotId && <FieldError>{errors.lotId.message}</FieldError>}
              </Field>
            )}
          </section>

          {/* 보관 위치 */}
          <section className='flex flex-col gap-2'>
            <div className='flex flex-wrap items-center justify-between gap-2'>
              <span className='text-sm font-medium'>보관 위치</span>
              <ModeToggle<LocationMode>
                label='보관 위치 입력 방식'
                value={locationMode}
                onChange={(v) => setValue("locationMode", v)}
                options={[
                  { value: "existing", label: "기존 위치 선택" },
                  { value: "new", label: "신규 등록" },
                  { value: "none", label: "지정 안 함" },
                ]}
              />
            </div>
            {locationMode === "existing" && (
              <Field>
                <NativeSelect
                  aria-label='보관 위치'
                  className='w-full'
                  {...register("locationId")}
                >
                  <option value=''>
                    {locations.isLoading ? "불러오는 중…" : "선택 (미지정 가능)"}
                  </option>
                  {locations.data?.items.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.name}
                    </option>
                  ))}
                </NativeSelect>
                {locations.isError && (
                  <FieldError>보관 위치 목록을 불러오지 못했습니다.</FieldError>
                )}
              </Field>
            )}
            {locationMode === "new" && (
              <div className='grid gap-3 sm:grid-cols-2'>
                <Field>
                  <FieldLabel htmlFor='new-location-code'>위치 코드</FieldLabel>
                  <Input
                    id='new-location-code'
                    placeholder='예: A-01-02'
                    {...register("newLocationCode", {
                      validate: (v) =>
                        watch("locationMode") !== "new" ||
                        required("위치 코드를 입력하세요.")(v),
                    })}
                  />
                  {errors.newLocationCode && (
                    <FieldError>{errors.newLocationCode.message}</FieldError>
                  )}
                </Field>
                <Field>
                  <FieldLabel htmlFor='new-location-name'>위치명</FieldLabel>
                  <Input
                    id='new-location-name'
                    {...register("newLocationName", {
                      validate: (v) =>
                        watch("locationMode") !== "new" ||
                        required("위치명을 입력하세요.")(v),
                    })}
                  />
                  {errors.newLocationName && (
                    <FieldError>{errors.newLocationName.message}</FieldError>
                  )}
                </Field>
              </div>
            )}
          </section>

          <Field>
            <FieldLabel htmlFor='stock-in-memo'>메모 (선택)</FieldLabel>
            <Textarea id='stock-in-memo' rows={2} {...register("memo")} />
          </Field>

          {submitError && (
            <p role='alert' className='text-sm text-destructive'>
              {submitError}
            </p>
          )}

          <div className='flex flex-wrap gap-2'>
            <Button type='submit' disabled={isBusy}>
              {isBusy ? "입고 처리 중…" : "입고 확정"}
            </Button>
          </div>
        </form>

        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>입고를 확정할까요?</AlertDialogTitle>
              <AlertDialogDescription>
                {item.name} {formatQuantity(quantity)} {item.baseUnit}
                {vendorName ? ` · ${vendorName}` : ""} 입고가 재고에 반영됩니다.
                확정 후에는 사용 전에 한해 입고 취소로만 되돌릴 수 있습니다.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>돌아가기</AlertDialogCancel>
              <AlertDialogAction onClick={handleSubmit(submit)}>
                확정
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
};
